import React from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { DealerProvider } from "../context/dealer/dealerContext";
import { useAuth } from "../context/authContext";

import DealerLogin from "../pages/Dealers/Login";
import DealerDashboard from "../pages/Dealers/Dashboard";
import VehicleDetails from "../pages/Dealers/VehicleDetails";
import DealerInspection from "../pages/Dealers/Inspection/Condition";
import DealerInspectionImages from "../pages/Dealers/Inspection/Images";
/**
 * ProtectedRoute is a wrapper component that redirects to the login page if the user is not authenticated.
 * It's used to protect routes that require user authentication.
 * @param {object} props - Props for the ProtectedRoute component.
 * @param {React.ReactNode} props.children - Child components to render if the user is authenticated.
 * @returns {React.ReactElement} A redirection to login or the children components based on authentication.
 */
const ProtectedRoute = ({ children }) => {
  const { currentUser } = useAuth();

  if (!currentUser) {
    // Redirect to the dealer login page if not authenticated
    return <Navigate to="/dealer/login" />;
  }

  return children;
};

/**
 * DealerRoutes defines the routing for dealer-related pages in the application.
 * It includes routes for login, dashboard, vehicle details, and inspections,
 * with appropriate protection for routes that require authentication.
 *
 * The DealerProvider context is used to wrap all dealer routes, providing them
 * with necessary context data and functionalities.
 * @returns {React.ReactElement} The Dealer routes wrapped in DealerProvider.
 */
const DealerRoutes = () => {
  return (
    <DealerProvider>
      <Routes>
        <Route path="login" element={<DealerLogin />} />
        <Route path="dashboard" element={<ProtectedRoute><DealerDashboard /></ProtectedRoute>} />
        <Route path="vehicle-details/:vehicleID" element={<ProtectedRoute><VehicleDetails /></ProtectedRoute>} />
        <Route path="inspection/condition/:vehicleID" element={<ProtectedRoute><DealerInspection /></ProtectedRoute>} />
        <Route path="inspection" >
          <Route index element={<DealerInspection />} />
          <Route path="condition" element={<DealerInspection />} />
          <Route path="images" element={<DealerInspectionImages />} />
        </Route>

        {/* Redirect /inspection to /inspection/condition */}
        <Route path="inspection" element={<Navigate to="inspection/condition" replace />} />
      </Routes>
    </DealerProvider>
  );
};

export default DealerRoutes;
